import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api, fmtDate, imgUrl } from "@/lib/api";
import { NewsletterBox } from "@/components/site/NewsletterBox";
import { Seo } from "@/components/site/Seo";

const CATS = ["tutte", "costi", "confronti", "guide", "recensioni"];

export default function Blog() {
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("tutte");
  const [pet, setPet] = useState("tutti");

  useEffect(() => {
    const t = setTimeout(() => api.get("/public/articles", { params: { q, category: cat, pet_type: pet } }).then((r) => setArticles(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, cat, pet]);

  const [featured, ...rest] = articles;

  return (
    <div className="mx-auto max-w-7xl px-4 lg:px-8 py-12" data-testid="blog-page">
      <Seo title="Blog | Guide e confronti assicurazioni cane e gatto — Polizzapet.it" description="Articoli indipendenti su costi veterinari, polizze pet, recensioni e confronti per proprietari di cani e gatti in Italia." />
      <p className="label-mono mb-3">Blog</p>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-8">Guide, costi e confronti</h1>
      <div className="flex flex-col lg:flex-row gap-3 lg:items-center mb-10">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="input !pl-9" placeholder="Cerca un argomento..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="blog-search-input" />
        </div>
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => <button key={c} onClick={() => setCat(c)} data-testid={`blog-cat-${c}`} className={`px-3 py-1.5 rounded-full text-sm border capitalize transition-colors ${cat === c ? "border-emerald-500 bg-emerald-500/10 text-emerald-500" : "border-border hover:bg-muted"}`}>{c}</button>)}
        </div>
        <div className="flex gap-2 lg:ml-auto">
          {["tutti", "cane", "gatto"].map((p) => <button key={p} onClick={() => setPet(p)} data-testid={`blog-pet-${p}`} className={`px-3 py-1.5 rounded-full text-sm border capitalize transition-colors ${pet === p ? "border-emerald-500 bg-emerald-500/10 text-emerald-500" : "border-border hover:bg-muted"}`}>{p}</button>)}
        </div>
      </div>

      {featured && (
        <Link to={`/blog/${featured.slug}`} className="group surface grid md:grid-cols-2 overflow-hidden mb-8 hover:border-emerald-500/50 transition-colors" data-testid="blog-featured">
          {featured.cover_image && <img src={imgUrl(featured.cover_image)} alt={featured.cover_alt || featured.title} className="h-64 md:h-full w-full object-cover" />}
          <div className="p-7 flex flex-col justify-center">
            <p className="label-mono mb-3">In evidenza · {featured.category} · {featured.reading_time} min</p>
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight group-hover:text-emerald-500 transition-colors">{featured.title}</h2>
            <p className="text-muted-foreground mt-3">{featured.excerpt}</p>
            <p className="text-xs text-muted-foreground mt-4">{featured.author} · {fmtDate(featured.published_at)}</p>
          </div>
        </Link>
      )}

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
        {rest.map((a) => (
          <Link key={a.id} to={`/blog/${a.slug}`} className="group surface overflow-hidden hover:border-emerald-500/50 transition-colors" data-testid={`blog-card-${a.slug}`}>
            {a.cover_image && <img src={imgUrl(a.cover_image)} alt={a.cover_alt || a.title} className="h-44 w-full object-cover" loading="lazy" />}
            <div className="p-5">
              <p className="label-mono mb-2">{a.category} · {a.reading_time} min</p>
              <h3 className="font-display font-semibold leading-snug group-hover:text-emerald-500 transition-colors">{a.title}</h3>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{a.excerpt}</p>
            </div>
          </Link>
        ))}
      </div>
      {articles.length === 0 && <p className="text-muted-foreground py-10" data-testid="blog-empty">Nessun articolo trovato.</p>}
      <div className="max-w-3xl mt-16"><NewsletterBox /></div>
    </div>
  );
}
